import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { useAuth } from "@/hooks/useAuth";
import { useWorkspace } from "@/hooks/useWorkspace";
import { useSeatPersonalization } from "@/hooks/useSeatPersonalization";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { CheckCircle2, Loader2, Sparkles, RotateCcw } from "lucide-react";
import { TemplateDialog } from "@/components/settings/TemplateDialog";
import type { PersonalizationTemplate } from "@/lib/personalizationTemplates";

const ROLES = [
  "Gründer / CEO",
  "Führungskraft / Abteilungsleitung",
  "Produkt / Strategie",
  "HR / People & Culture",
  "Individual Contributor",
  "Sonstiges",
];

const DECISION_AREAS = [
  { id: "strategy", label: "Strategie & Positionierung" },
  { id: "product", label: "Produkt & Innovation" },
  { id: "growth", label: "Wachstum & Skalierung" },
  { id: "team", label: "Team & Führung" },
  { id: "operations", label: "Operations & Prozesse" },
  { id: "finance", label: "Finanzen & Investment" },
];

const TEAM_SIZES = [
  { id: "1-10", label: "1-10 Personen" },
  { id: "11-50", label: "11-50 Personen" },
  { id: "51-200", label: "51-200 Personen" },
  { id: "200+", label: "200+ Personen" },
];

const COMPANY_PHASES = [
  { id: "founding", label: "Gründungsphase" },
  { id: "growth", label: "Wachstumsphase" },
  { id: "scaling", label: "Skalierungsphase" },
  { id: "established", label: "Etabliert" },
];

const DECISION_STYLES = [
  { id: "data-driven", label: "Datengetrieben" },
  { id: "intuitive", label: "Intuitiv" },
  { id: "collaborative", label: "Kollaborativ" },
  { id: "pragmatic", label: "Schnell & pragmatisch" },
];

const PRIORITIES = [
  { id: "accelerate-growth", label: "Wachstum beschleunigen" },
  { id: "reduce-costs", label: "Kosten senken" },
  { id: "improve-product", label: "Produkt verbessern" },
  { id: "scale-team", label: "Team skalieren" },
  { id: "optimize-processes", label: "Prozesse optimieren" },
  { id: "find-focus", label: "Fokus finden" },
];

const Seat = () => {
  const { user } = useAuth();
  const { workspace } = useWorkspace();
  const { personalization, loading, savePersonalization } = useSeatPersonalization(
    workspace?.id || null,
    user?.id || null
  );

  const [selectedRole, setSelectedRole] = useState<string>("");
  const [title, setTitle] = useState("");
  const [selectedAreas, setSelectedAreas] = useState<string[]>([]);
  const [teamSize, setTeamSize] = useState<string>("");
  const [companyPhase, setCompanyPhase] = useState<string>("");
  const [decisionStyles, setDecisionStyles] = useState<string[]>([]);
  const [communicationPref, setCommunicationPref] = useState<string>("automatic");
  const [detailLevel, setDetailLevel] = useState<string>("adaptive");
  const [priorities, setPriorities] = useState<string[]>([]);
  const [isSaving, setIsSaving] = useState(false);
  const [showTemplateDialog, setShowTemplateDialog] = useState(false);

  useEffect(() => {
    if (personalization) {
      setSelectedRole(personalization.job_role || "");
      setTitle(personalization.job_title || "");
      setSelectedAreas(personalization.preferred_decision_areas || []);
      setTeamSize(personalization.team_size || "");
      setCompanyPhase(personalization.company_phase || "");
      setDecisionStyles(personalization.decision_style || []);
      setCommunicationPref(personalization.communication_preference || "automatic");
      setDetailLevel(personalization.detail_level || "adaptive");
      setPriorities(personalization.current_priorities || []);
    }
  }, [personalization]);

  const toggleArea = (areaId: string) => {
    setSelectedAreas(prev =>
      prev.includes(areaId)
        ? prev.filter(id => id !== areaId)
        : [...prev, areaId]
    );
  };

  const toggleDecisionStyle = (styleId: string) => {
    setDecisionStyles(prev =>
      prev.includes(styleId)
        ? prev.filter(id => id !== styleId)
        : [...prev, styleId]
    );
  };

  const togglePriority = (priorityId: string) => {
    setPriorities(prev =>
      prev.includes(priorityId)
        ? prev.filter(id => id !== priorityId)
        : [...prev, priorityId]
    );
  };

  const handleTemplateSelect = (template: PersonalizationTemplate) => {
    if (template.data.job_role) setSelectedRole(template.data.job_role);
    if (template.data.preferred_decision_areas) setSelectedAreas(template.data.preferred_decision_areas);
    if (template.data.team_size) setTeamSize(template.data.team_size);
    if (template.data.company_phase) setCompanyPhase(template.data.company_phase);
    if (template.data.decision_style) setDecisionStyles(template.data.decision_style);
    if (template.data.communication_preference) setCommunicationPref(template.data.communication_preference);
    if (template.data.detail_level) setDetailLevel(template.data.detail_level);
    if (template.data.current_priorities) setPriorities(template.data.current_priorities);
    toast.success("Template angewendet");
  };

  const handleReset = () => {
    setSelectedRole("");
    setTitle("");
    setSelectedAreas([]);
    setTeamSize("");
    setCompanyPhase("");
    setDecisionStyles([]);
    setCommunicationPref("automatic");
    setDetailLevel("adaptive");
    setPriorities([]);
    toast.info("Alle Einstellungen zurückgesetzt");
  };

  const handleSave = async () => {
    if (!selectedRole) {
      toast.error("Bitte wähle eine Rolle aus");
      return;
    }

    setIsSaving(true);
    try {
      await savePersonalization({
        job_role: selectedRole,
        job_title: title || undefined,
        preferred_decision_areas: selectedAreas,
        team_size: teamSize || undefined,
        company_phase: companyPhase || undefined,
        decision_style: decisionStyles,
        communication_preference: communicationPref,
        detail_level: detailLevel,
        current_priorities: priorities,
      });
      toast.success("Seat-Personalisierung erfolgreich gespeichert");
    } catch (error) {
      console.error('Error saving personalization:', error);
      toast.error("Fehler beim Speichern der Personalisierung");
    } finally {
      setIsSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="max-w-4xl space-y-6 pb-20 md:pb-6">

      <Card>
        <CardHeader>
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div>
              <CardTitle>Seat-Personalisierung</CardTitle>
              <CardDescription>
                Passe deine Rolle und bevorzugten Entscheidungsbereiche an
              </CardDescription>
            </div>
            <div className="flex flex-wrap gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={handleReset}
                className="gap-2"
              >
                <RotateCcw className="h-4 w-4" />
                Zurücksetzen
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowTemplateDialog(true)}
                className="gap-2"
              >
                <Sparkles className="h-4 w-4" />
                Template laden
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Role Selection */}
          <div className="space-y-4">
            <Label className="text-base font-semibold">Deine Rolle im Unternehmen *</Label>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {ROLES.map((role) => (
                <button
                  key={role}
                  onClick={() => setSelectedRole(role)}
                  className={cn(
                    "flex items-center justify-center rounded-lg border-2 p-4 transition-all duration-200 hover:scale-[1.02] min-h-[64px]",
                    selectedRole === role
                      ? "border-primary bg-primary/5"
                      : "border-border hover:border-primary/50"
                  )}
                >
                  <p
                    className={cn(
                      "font-semibold text-base leading-tight",
                      selectedRole === role ? "text-foreground" : "text-foreground/90"
                    )}
                  >
                    {role}
                  </p>
                </button>
              ))}
            </div>
          </div>

          {/* Job Title */}
          <div className="space-y-3">
            <Label htmlFor="title" className="text-base">Berufsbezeichnung (optional)</Label>
            <Input
              id="title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="z.B. Head of Product"
              className="h-12 text-base"
              maxLength={100}
            />
          </div>

          {/* Preferred Decision Areas */}
          <div className="space-y-4">
            <div>
              <Label className="text-base font-semibold">Bevorzugte Entscheidungsbereiche</Label>
              <p className="text-sm text-muted-foreground mt-1">
                Diese Bereiche erscheinen als Schnellzugriff im Portal
              </p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {DECISION_AREAS.map((area) => (
                <button
                  key={area.id}
                  onClick={() => toggleArea(area.id)}
                  className={cn(
                    "flex items-center justify-between rounded-lg border-2 p-4 transition-all duration-200 hover:scale-[1.02] min-h-[64px]",
                    selectedAreas.includes(area.id)
                      ? "border-primary bg-primary/5"
                      : "border-border hover:border-primary/50"
                  )}
                >
                  <p className={cn("font-semibold text-base leading-tight", selectedAreas.includes(area.id) ? "text-foreground" : "text-foreground/90")}>
                    {area.label}
                  </p>
                  {selectedAreas.includes(area.id) && (
                    <CheckCircle2 className="h-5 w-5 text-primary flex-shrink-0 ml-2" />
                  )}
                </button>
              ))}
            </div>
          </div>

          {/* Team Size */}
          <div className="space-y-4">
            <Label className="text-base font-semibold">Teamgröße</Label>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {TEAM_SIZES.map((size) => (
                <button
                  key={size.id}
                  onClick={() => setTeamSize(size.id)}
                  className={cn(
                    "rounded-lg border-2 p-3 transition-all duration-200 hover:scale-[1.02]",
                    teamSize === size.id ? "border-primary bg-primary/5" : "border-border hover:border-primary/50"
                  )}
                >
                  <p className={cn("font-semibold text-sm", teamSize === size.id ? "text-foreground" : "text-foreground/90")}>
                    {size.label}
                  </p>
                </button>
              ))}
            </div>
          </div>

          {/* Company Phase */}
          <div className="space-y-4">
            <Label className="text-base font-semibold">Unternehmensphase</Label>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {COMPANY_PHASES.map((phase) => (
                <button
                  key={phase.id}
                  onClick={() => setCompanyPhase(phase.id)}
                  className={cn(
                    "rounded-lg border-2 p-3 transition-all duration-200 hover:scale-[1.02]",
                    companyPhase === phase.id ? "border-primary bg-primary/5" : "border-border hover:border-primary/50"
                  )}
                >
                  <p className={cn("font-semibold text-sm", companyPhase === phase.id ? "text-foreground" : "text-foreground/90")}>
                    {phase.label}
                  </p>
                </button>
              ))}
            </div>
          </div>

          {/* Decision Styles */}
          <div className="space-y-4">
            <div>
              <Label className="text-base font-semibold">Entscheidungsstil</Label>
              <p className="text-sm text-muted-foreground mt-1">
                Mehrfachauswahl möglich
              </p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {DECISION_STYLES.map((style) => (
                <button
                  key={style.id}
                  onClick={() => toggleDecisionStyle(style.id)}
                  className={cn(
                    "flex items-center justify-between rounded-lg border-2 p-4 transition-all duration-200 hover:scale-[1.02]",
                    decisionStyles.includes(style.id) ? "border-primary bg-primary/5" : "border-border hover:border-primary/50"
                  )}
                >
                  <p className={cn("font-semibold text-base", decisionStyles.includes(style.id) ? "text-foreground" : "text-foreground/90")}>
                    {style.label}
                  </p>
                  {decisionStyles.includes(style.id) && (
                    <CheckCircle2 className="h-5 w-5 text-primary flex-shrink-0 ml-2" />
                  )}
                </button>
              ))}
            </div>
          </div>

          {/* Current Priorities */}
          <div className="space-y-4">
            <div>
              <Label className="text-base font-semibold">Aktuelle Prioritäten</Label>
              <p className="text-sm text-muted-foreground mt-1">
                Mehrfachauswahl möglich
              </p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {PRIORITIES.map((priority) => (
                <button
                  key={priority.id}
                  onClick={() => togglePriority(priority.id)}
                  className={cn(
                    "flex items-center justify-between rounded-lg border-2 p-3 transition-all duration-200 hover:scale-[1.02]",
                    priorities.includes(priority.id) ? "border-primary bg-primary/5" : "border-border hover:border-primary/50"
                  )}
                >
                  <p className={cn("font-semibold text-sm", priorities.includes(priority.id) ? "text-foreground" : "text-foreground/90")}>
                    {priority.label}
                  </p>
                  {priorities.includes(priority.id) && (
                    <CheckCircle2 className="h-5 w-5 text-primary flex-shrink-0 ml-2" />
                  )}
                </button>
              ))}
            </div>
          </div>

          <Button
            onClick={handleSave}
            disabled={!selectedRole || isSaving}
            className="w-full sm:w-auto"
          >
            {isSaving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Änderungen speichern
          </Button>
        </CardContent>
      </Card>

      <TemplateDialog
        open={showTemplateDialog}
        onOpenChange={setShowTemplateDialog}
        onSelectTemplate={handleTemplateSelect}
      />
    </div>
  );
};

export default Seat;
